import { Link } from "wouter";
import { ArrowRight, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useEstateReadyScore, getEssentials } from "@/hooks/useEstateReadyScore";
import { EXECUTOR_TERM } from "@shared/constants/terminology";
import { SAGE } from "@shared/brand";

interface Props {
  clientId: string;
}


/**
 * The merged "Ten Essentials" dashboard card (s79, A1) — replaces the separate
 * StartHereCard ("What's next") + DualProgressCard ("How much is in place").
 *
 * Renders a score header (X of N in place, N = applicable essentials) plus the
 * 10 essentials in FIXED planner-card order (not status-sorted): done items are
 * de-emphasized with a check; outstanding items are prominent + clickable,
 * deep-linking to the first missing piece. The conditional care-plan essential
 * appears only when applicable (the hook drops N/A essentials).
 *
 * Presentation is provisional — the later redesign pass owns polish and the
 * About You → 10 → next flow. Keep `id="start-here"` so existing anchors resolve.
 */
export function EstateEssentialsCard({ clientId }: Props) {
  const { data } = useEstateReadyScore(clientId);
  const essentials = getEssentials(data);

  // Avoid a flash before the score loads.
  if (!data) return null;

  const total = data.total;
  const score = data.score;
  const allDone = essentials.length > 0 && essentials.every(e => e.status === "done");

  return (
    <Card id="start-here" className="border-l-4 h-full" style={{ borderLeftColor: SAGE[600] }}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">The 10 essentials</CardTitle>
          {/* s156 F (card copy, approved): under the D-156 bar an essential ticks only when every
              bound section is genuinely finished, so most planners sit well below 10. "N of 10 in
              place" then reads as a score being lost rather than a list being worked. Framing the
              count as REMAINING work keeps the same number honest and points at what to do next.
              The row check + strikethrough are unchanged — they were already honest. */}
          <span className="text-sm font-semibold" style={{ color: SAGE[600] }} data-testid="essentials-count">
            {total - score === 0 ? `All ${total} in place` : `${total - score} still need${total - score === 1 ? "s" : ""} you`}
          </span>
        </div>
        <Progress
          value={total > 0 ? Math.round((score / total) * 100) : 0}
          className="h-2 mt-2 [&>div]:bg-[#4F6B51]"
        />
      </CardHeader>
      <CardContent className="pt-0">
        {/* allDone is a HEADER above the rows, never a replacement — the 10 rows
            always render (locked decision: they're navigation — revisit / update /
            change applicability — not just a status readout). */}
        {allDone && (
          <div className="flex items-start gap-2.5 mb-3 pb-3 border-b">
            <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: SAGE[600] }} />
            <p className="text-sm text-muted-foreground leading-relaxed">
              The essentials are in place — your family and executor will know where to look.
            </p>
          </div>
        )}
        {/* s94 (Stephanie F1): a one-line nudge so it's clear the rows are tappable.
            1E: executor term resolves through EXECUTOR_TERM (jurisdiction seam). */}
        {/* s176 (COMPLETE-STATE-1): "get started" read wrong at 10/10, four lines below
            the allDone banner saying the opposite. The rows stay navigable either way —
            only the nudge changes. */}
        <p className="text-xs text-muted-foreground mb-2">
          {allDone
            ? `These are the top 10 things your ${EXECUTOR_TERM} needs. Click on any item below to revisit or update it.`
            : `These are the top 10 things your ${EXECUTOR_TERM} needs. Click on any item below to get started.`}
        </p>
        {/* Single-column rows (s80 — the essentials card now sits in the left half of
            the dashboard's 2-col working area, so its rows stay one column). */}
        <ul className="space-y-0.5">
          {essentials.map((e) => {
            const done = e.status === "done";
            // Every row is a link — a done essential stays navigable so the planner
            // can revisit or update it. The check + strikethrough still mark "done".
            return (
              <li key={e.key}>
                <Link
                  href={e.deepLink}
                  className={`group flex gap-2.5 rounded-md -mx-2 px-2 py-2 text-sm hover:bg-muted transition-colors ${
                    e.subline ? "items-start" : "items-center"
                  }`}
                  data-testid={`essential-${e.key}`}
                  data-status={done ? "done" : "outstanding"}
                >
                  {/* On a two-line row the marker and arrow track the LABEL line, not the
                      block's centre — otherwise they drift down beside the subline. */}
                  {done ? (
                    <Check className={`h-4 w-4 shrink-0 ${e.subline ? "mt-0.5" : ""}`} style={{ color: SAGE[600] }} />
                  ) : (
                    <span className={`h-4 w-4 rounded-full border border-muted-foreground/40 shrink-0 ${e.subline ? "mt-0.5" : ""}`} />
                  )}
                  {/* s159: an outstanding essential may carry a partial-state subline —
                      the headline fact is recorded, the section is not finished. The
                      strikethrough stays on the label only (a subline never renders on a
                      done row), and the copy comes from the spec, never from here. */}
                  <span className="flex-1 min-w-0">
                    <span
                      className={
                        done
                          ? "block text-muted-foreground line-through decoration-muted-foreground/40"
                          : "block text-foreground"
                      }
                    >
                      {e.label}
                    </span>
                    {e.subline && (
                      <span
                        className="block text-xs text-muted-foreground leading-snug mt-0.5"
                        data-testid={`essential-subline-${e.key}`}
                      >
                        {e.subline}
                      </span>
                    )}
                  </span>
                  <ArrowRight
                    className={`h-4 w-4 shrink-0 text-muted-foreground/40 group-hover:text-primary transition-colors ${
                      e.subline ? "mt-0.5" : ""
                    }`}
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
